<template>
  <div class="scholar-entity-page flex-col">
    <div class="scholar-entity-shell flex-col">
      <ScholarHeader />

      <main class="scholar-entity-main">
        <div class="scholar-entity-toolbar">
          <button type="button" class="scholar-back-btn" data-track-text="返回上一页" aria-label="返回上一页" @click="backToSearch">
            <i class="el-icon-back"></i>
            返回上一页
          </button>
        </div>

        <section v-loading="loading" class="scholar-entity-content">
          <el-alert
            v-if="errorMessage"
            class="scholar-entity-error"
            type="error"
            :closable="false"
            :title="errorMessage"
            show-icon
          />

          <template v-if="entity">
            <div class="scholar-entity-head">
              <div class="scholar-entity-title-block">
                <span class="scholar-entity-type">{{ entityTypeText }}</span>
                <h1>{{ entity.name || fallbackName }}</h1>
                <p v-if="entity.hint || extraText">{{ entity.hint || extraText }}</p>
              </div>
              <div class="scholar-entity-actions">
                <el-button type="primary" data-track-text="检索实体相关文献" aria-label="检索实体相关文献" @click="searchEntityWorks">
                  检索相关文献
                </el-button>
                <el-button v-if="entity.url || homepageUrl" data-track-text="打开实体外部主页" aria-label="打开实体外部主页" @click="openExternal(homepageUrl || entity.url)">
                  <i class="el-icon-link"></i>
                  {{ externalOpenText }}
                </el-button>
                <el-button class="scholar-entity-more-btn" data-track-text="复制实体概览" aria-label="复制实体概览" @click="copyEntityProfile">
                  <i class="el-icon-document-copy"></i>
                  复制概览
                </el-button>
                <el-button class="scholar-entity-more-btn" data-track-text="导出实体概览" aria-label="导出实体概览" @click="downloadEntityProfile">
                  <i class="el-icon-download"></i>
                  导出概览
                </el-button>
              </div>
            </div>

            <div class="scholar-entity-metrics">
              <div>
                <strong>{{ formatNumber(entity.worksCount || metric('worksCount') || 0) }}</strong>
                <span>作品数</span>
              </div>
              <div>
                <strong>{{ formatNumber(entity.citedByCount || metric('citedByCount') || 0) }}</strong>
                <span>引用数</span>
              </div>
              <div>
                <strong>{{ metric('hIndex') || '-' }}</strong>
                <span>H-index</span>
              </div>
              <div>
                <strong>{{ metric('i10Index') || '-' }}</strong>
                <span>I10-index</span>
              </div>
            </div>

            <div class="scholar-entity-layout">
              <aside class="scholar-entity-side">
                <section class="scholar-entity-section">
                  <h2>基础信息</h2>
                  <dl class="scholar-field-list">
                    <div v-if="entity.externalId">
                      <dt>{{ externalIdLabel }}</dt>
                      <dd>{{ entity.externalId }}</dd>
                    </div>
                    <div v-if="extra('countryCode')">
                      <dt>国家/地区</dt>
                      <dd>{{ extra('countryCode') }}</dd>
                    </div>
                    <div v-if="extra('type')">
                      <dt>类型</dt>
                      <dd>{{ extra('type') }}</dd>
                    </div>
                    <div v-if="extra('issnL')">
                      <dt>ISSN-L</dt>
                      <dd>{{ extra('issnL') }}</dd>
                    </div>
                    <div v-if="extra('hostOrganizationName')">
                      <dt>主办机构</dt>
                      <dd>{{ extra('hostOrganizationName') }}</dd>
                    </div>
                    <div v-if="extra('domain') || extra('field') || extra('subfield')">
                      <dt>学科层级</dt>
                      <dd>{{ [extra('domain'), extra('field'), extra('subfield')].filter(Boolean).join(' / ') }}</dd>
                    </div>
                  </dl>
                </section>

                <section v-if="entityDescription" class="scholar-entity-section">
                  <h2>简介</h2>
                  <p class="scholar-entity-description">{{ entityDescription }}</p>
                </section>

                <section v-if="entityExternalIds.length" class="scholar-entity-section">
                  <h2>外部标识</h2>
                  <dl class="scholar-field-list">
                    <div v-for="item in entityExternalIds" :key="item.key">
                      <dt>{{ item.label }}</dt>
                      <dd>{{ item.value }}</dd>
                    </div>
                  </dl>
                </section>

                <section v-if="entityRoles.length" class="scholar-entity-section">
                  <h2>关联角色</h2>
                  <div class="scholar-role-list">
                    <button
                      v-for="role in entityRoles"
                      :key="role.role + '-' + role.id"
                      type="button"
                      :disabled="!roleTargetType(role)"
                      data-track-text="打开实体关联角色"
                      aria-label="打开实体关联角色"
                      @click="openRole(role)"
                    >
                      <span>{{ roleText(role.role) }}</span>
                      <strong>{{ formatNumber(role.worksCount || 0) }} 作品</strong>
                    </button>
                  </div>
                </section>

                <section v-if="entityRepositories.length" class="scholar-entity-section">
                  <h2>相关仓储</h2>
                  <div class="scholar-repository-list">
                    <button
                      v-for="repository in entityRepositories"
                      :key="repository.id || repository.name"
                      type="button"
                      :disabled="!repositoryTargetId(repository)"
                      data-track-text="打开实体相关仓储"
                      aria-label="打开实体相关仓储"
                      @click="openRepository(repository)"
                    >
                      <span>{{ repository.name || repository.id }}</span>
                      <em v-if="repository.hostOrganization">{{ repository.hostOrganization }}</em>
                    </button>
                  </div>
                </section>

                <section v-if="countsByYear.length" class="scholar-entity-section">
                  <div class="scholar-entity-section-head">
                    <h2>年度趋势</h2>
                    <div class="scholar-entity-section-actions">
                      <button type="button" data-track-text="复制实体年度趋势" aria-label="复制实体年度趋势" @click="copyYearTrend">
                        <i class="el-icon-document-copy"></i>
                        复制
                      </button>
                      <button type="button" data-track-text="导出实体年度趋势CSV" aria-label="导出实体年度趋势CSV" @click="downloadYearTrend">
                        <i class="el-icon-download"></i>
                        CSV
                      </button>
                    </div>
                  </div>
                  <div class="scholar-year-bars">
                    <div v-for="item in countsByYear" :key="item.year" class="scholar-year-row">
                      <span>{{ item.year }}</span>
                      <em><i :style="{ width: yearPercent(item) }"></i></em>
                      <strong>{{ formatNumber(item.citedByCount || item.worksCount || 0) }}</strong>
                    </div>
                  </div>
                </section>

                <section v-if="entityTopics.length" class="scholar-entity-section">
                  <h2>相关主题</h2>
                  <div class="scholar-topic-list">
                    <button
                      v-for="topic in entityTopics"
                      :key="topic.id || topic.name"
                      type="button"
                      data-track-text="打开实体相关主题"
                      aria-label="打开实体相关主题"
                      @click="openTopic(topic)"
                    >
                      {{ topic.name || topic.id }}
                    </button>
                  </div>
                </section>
              </aside>

              <section v-loading="worksLoading" class="scholar-entity-works">
                <div class="scholar-entity-work-head">
                  <h2>代表文献</h2>
                  <div class="scholar-entity-work-actions">
                    <span>{{ formatNumber(total) }} 条结果</span>
                    <button
                      v-if="works.length"
                      type="button"
                      class="scholar-entity-work-tools-btn"
                      :class="{ active: worksActionsVisible }"
                      data-track-text="展开实体文献导出复制"
                      aria-label="展开实体文献导出复制"
                      @click="toggleWorksActions"
                    >
                      导出/复制
                      <i class="el-icon-arrow-down"></i>
                    </button>
                  </div>
                </div>
                <div v-if="works.length && worksActionsVisible" class="scholar-entity-works-action-panel">
                  <section>
                    <h3>复制</h3>
                    <div>
                      <button type="button" data-track-text="复制实体文献清单" aria-label="复制实体文献清单" @click="runWorksMoreCommand('copy:markdown')">文献清单</button>
                      <button type="button" data-track-text="复制实体文献BibTeX" aria-label="复制实体文献BibTeX" @click="runWorksMoreCommand('copy:bibtex')">BibTeX</button>
                      <button type="button" data-track-text="复制实体文献RIS" aria-label="复制实体文献RIS" @click="runWorksMoreCommand('copy:ris')">RIS / EndNote</button>
                    </div>
                  </section>
                  <section>
                    <h3>导出</h3>
                    <div>
                      <button type="button" data-track-text="导出实体文献Markdown" aria-label="导出实体文献Markdown" @click="runWorksMoreCommand('export:markdown')">Markdown 清单</button>
                      <button type="button" data-track-text="导出实体文献BibTeX" aria-label="导出实体文献BibTeX" @click="runWorksMoreCommand('export:bibtex')">BibTeX</button>
                      <button type="button" data-track-text="导出实体文献RIS" aria-label="导出实体文献RIS" @click="runWorksMoreCommand('export:ris')">RIS / EndNote</button>
                      <button type="button" data-track-text="导出实体文献CSV" aria-label="导出实体文献CSV" @click="runWorksMoreCommand('export:csv')">CSV 元数据</button>
                      <button type="button" data-track-text="导出实体文献筛选表CSV" aria-label="导出实体文献筛选表CSV" @click="runWorksMoreCommand('export:screening')">筛选表 CSV</button>
                    </div>
                  </section>
                </div>
                <article
                  v-for="work in works"
                  :key="work.workKey"
                  class="scholar-entity-work"
                  data-track-text="打开实体代表文献"
                  aria-label="打开实体代表文献"
                  @click="openWork(work)"
                >
                  <div class="scholar-entity-work-main">
                    <strong>{{ work.title || 'Untitled work' }}</strong>
                    <p>{{ work.publicationYear || '-' }} · {{ work.sourceName || '未知来源' }} · 引 {{ formatNumber(work.citedByCount || 0) }}</p>
                    <div v-if="work.doi" class="scholar-entity-work-doi">
                      <span>DOI</span>
                      <button
                        type="button"
                        data-track-text="复制实体文献DOI"
                        aria-label="复制实体文献DOI"
                        :title="`复制 DOI：${work.doi}`"
                        @click.stop="copyWorkDoi(work)"
                      >
                        {{ work.doi }}
                      </button>
                    </div>
                    <div v-if="workAvailabilityChips(work).length" class="scholar-entity-work-chips">
                      <span
                        v-for="chip in workAvailabilityChips(work)"
                        :key="chip.key"
                        class="scholar-entity-work-chip"
                        :class="chip.tone"
                      >
                        {{ chip.label }}
                      </span>
                    </div>
                    <p v-if="work.abstractText" class="scholar-work-abstract">{{ shorten(work.abstractText, 180) }}</p>
                  </div>
                  <div class="scholar-entity-work-side" data-track-ignore @click.stop>
                    <span v-if="work.isOa" class="scholar-oa-badge">OA</span>
                    <button
                      v-if="work.pdfUrl"
                      type="button"
                      class="scholar-entity-pdf-btn"
                      :disabled="isWorkPdfDownloading(work)"
                      title="下载 PDF"
                      aria-label="下载 PDF"
                      data-track-text="下载实体代表文献PDF"
                      @click="downloadWorkPdf(work, $event)"
                    >
                      <i :class="isWorkPdfDownloading(work) ? 'el-icon-loading' : 'el-icon-download'"></i>
                      PDF
                    </button>
                    <button
                      type="button"
                      class="scholar-entity-cite-btn"
                      :class="{ active: isWorkCitationPanelOpen(work) }"
                      data-track-text="展开实体代表文献引用"
                      aria-label="展开实体代表文献引用"
                      @click="toggleWorkCitationPanel(work)"
                    >
                      <i class="el-icon-document-copy"></i>
                      引用
                      <i class="el-icon-arrow-down"></i>
                    </button>
                  </div>
                  <div
                    v-if="isWorkCitationPanelOpen(work)"
                    class="scholar-entity-work-citation-panel"
                    data-track-ignore
                    @click.stop
                  >
                    <strong>引用与导出</strong>
                    <div>
                      <button type="button" data-track-text="复制实体文献GBT引用" aria-label="复制实体文献GBT引用" @click="runSingleWorkCitationCommand('copy:gbt7714', work)">复制 GB/T</button>
                      <button type="button" data-track-text="复制实体文献APA引用" aria-label="复制实体文献APA引用" @click="runSingleWorkCitationCommand('copy:apa', work)">复制 APA</button>
                      <button type="button" data-track-text="复制实体文献BibTeX引用" aria-label="复制实体文献BibTeX引用" @click="runSingleWorkCitationCommand('copy:bibtex', work)">复制 BibTeX</button>
                      <button type="button" data-track-text="复制实体文献RIS引用" aria-label="复制实体文献RIS引用" @click="runSingleWorkCitationCommand('copy:ris', work)">复制 RIS / EndNote</button>
                      <button type="button" data-track-text="导出实体文献BibTeX引用" aria-label="导出实体文献BibTeX引用" @click="runSingleWorkCitationCommand('export:bibtex', work)">导出 BibTeX</button>
                      <button type="button" data-track-text="导出实体文献RIS引用" aria-label="导出实体文献RIS引用" @click="runSingleWorkCitationCommand('export:ris', work)">导出 RIS / EndNote</button>
                      <button type="button" data-track-text="导出实体文献CSV元数据" aria-label="导出实体文献CSV元数据" @click="runSingleWorkCitationCommand('export:csv', work)">导出 CSV 元数据</button>
                    </div>
                  </div>
                </article>
                <button
                  v-if="canLoadMoreWorks"
                  type="button"
                  class="scholar-entity-more"
                  :disabled="worksLoadingMore"
                  data-track-text="加载更多实体代表文献"
                  aria-label="加载更多实体代表文献"
                  @click="loadMoreWorks"
                >
                  {{ worksLoadingMore ? '加载中...' : '加载更多代表文献' }}
                </button>
                <el-empty v-if="!worksLoading && !works.length" description="暂无相关文献"></el-empty>
              </section>
            </div>
          </template>

          <el-empty v-else-if="!loading && !errorMessage" description="暂无实体信息"></el-empty>
        </section>
      </main>
      <ScholarFooter />
    </div>
  </div>
</template>

<script>
import { Base64 } from 'js-base64'
import { authServiceClient, translateServiceClient } from '@/tools/api/service'
import {
  outRequest,
  userPowerRequest,
  userStatusRequest
} from '@/tools/api/auth-api'
import {
  checkRoamingRequest
} from '@/tools/api/translate-api'
import {
  downloadScholarWorkPdf,
  getScholarEntity,
  searchScholar
} from '../tools/api/scholar-api'
import { formatWorks } from '../tools/scholar-citation'
import { openExternalWithConfirm } from '../tools/external-link'
import { restoreScholarScroll, safeScholarReturnUrl, scholarReturnScrollTop } from '../tools/scholar-route'
import ScholarHeader from '../components/ScholarHeader.vue'
import ScholarFooter from '../components/ScholarFooter.vue'

export default {
  name: 'ScholarEntityDetail',
  components: {
    ScholarHeader,
    ScholarFooter
  },
  data() {
    return {
      loading: false,
      worksLoading: false,
      worksLoadingMore: false,
      entity: null,
      works: [],
      worksPage: 1,
      worksSize: 10,
      total: 0,
      errorMessage: '',
      userInfo: '',
      groupUserInfo: '',
      groupUserInfos: [],
      showloginOut: false,
      showRoaming: false,
      qrcodeUrl: '',
      userId: '',
      userType: '',
      hasH5: false,
      isShowInRoaming: false,
      isRoamingTimer: undefined,
      isRoamingTimerOut: undefined,
      pdfDownloadingKeys: {},
      worksActionsVisible: false,
      activeCitationWorkKey: ''
    }
  },
  computed: {
    provider() {
      return this.$route.query.provider || 'openalex'
    },
    entityType() {
      return this.normalizeEntityType(this.$route.query.entityType || this.$route.query.type || '')
    },
    entityId() {
      return this.$route.query.id || ''
    },
    fallbackName() {
      return this.$route.query.name || this.entityId || '未知实体'
    },
    entityTypeText() {
      const map = {
        authors: '作者',
        institutions: '机构',
        sources: '期刊/来源',
        topics: '主题',
        keywords: '关键词',
        publishers: '出版商',
        funders: '基金机构'
      }
      return map[this.entityType] || '实体'
    },
    externalIdLabel() {
      const map = {
        authors: 'ORCID',
        institutions: 'ROR',
        sources: 'ISSN-L',
        funders: '外部标识'
      }
      return map[this.entityType] || '外部标识'
    },
    externalOpenText() {
      const map = {
        authors: '打开作者主页',
        institutions: '打开机构主页',
        sources: '打开来源',
        publishers: '打开出版商',
        funders: '打开基金机构'
      }
      return map[this.entityType] || '打开详情'
    },
    homepageUrl() {
      return this.extra('homepageUrl')
    },
    worksApiUrl() {
      return this.extra('worksApiUrl') || ''
    },
    entityDescription() {
      return this.extra('description') || ''
    },
    entityExternalIds() {
      const ids = this.extra('ids') || {}
      if (!ids || typeof ids !== 'object' || Array.isArray(ids)) {
        return []
      }
      return Object.keys(ids)
        .map(key => ({
          key,
          label: this.entityIdLabel(key),
          value: this.entityIdValue(ids[key])
        }))
        .filter(item => item.value)
        .slice(0, 12)
    },
    entityRoles() {
      const values = this.extra('roles') || []
      return Array.isArray(values) ? values.filter(Boolean).slice(0, 8) : []
    },
    entityRepositories() {
      const values = this.extra('repositories') || []
      return Array.isArray(values) ? values.filter(Boolean).slice(0, 8) : []
    },
    extraText() {
      return [this.extra('type'), this.extra('countryCode'), this.extra('issnL')].filter(Boolean).join(' · ')
    },
    countsByYear() {
      const values = this.extra('countsByYear') || []
      return Array.isArray(values) ? values.slice(0, 10) : []
    },
    entityTopics() {
      const values = this.extra('topics') || []
      return Array.isArray(values) ? values.slice(0, 12) : []
    },
    maxYearValue() {
      return Math.max(1, ...this.countsByYear.map(item => Number(item.citedByCount || item.worksCount || 0)))
    },
    canLoadMoreWorks() {
      return this.works.length > 0 && this.works.length < this.total && !this.worksLoading && !this.worksLoadingMore
    }
  },
  watch: {
    '$route.query': {
      handler() {
        this.scrollToPageTop()
        this.loadEntity()
      },
      immediate: true
    }
  },
  created() {
    this.syncUserInfoFromStorage()
    this.syncHeaderUserStatus()
  },
  mounted() {
    window.addEventListener('message', this.handleAuthMessage)
  },
  beforeDestroy() {
    window.removeEventListener('message', this.handleAuthMessage)
    clearInterval(this.isRoamingTimer)
    clearTimeout(this.isRoamingTimerOut)
  },
  methods: {
    scrollToPageTop() {
      this.$nextTick(() => {
        if (typeof window !== 'undefined' && window.scrollTo) {
          window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
        }
        if (typeof document !== 'undefined') {
          if (document.documentElement) {
            document.documentElement.scrollTop = 0
          }
          if (document.body) {
            document.body.scrollTop = 0
          }
        }
      })
    },
    async loadEntity() {
      if (!this.entityType || !this.entityId) {
        this.errorMessage = '缺少实体参数'
        this.entity = null
        return
      }
      this.loading = true
      this.errorMessage = ''
      this.entity = null
      this.works = []
      this.worksPage = 1
      this.total = 0
      try {
        this.entity = await getScholarEntity(this.provider, this.entityType, this.entityId)
        this.loadWorks(false)
      } catch (e) {
        this.errorMessage = this.resolveErrorMessage(e, '实体详情加载失败')
      } finally {
        this.loading = false
      }
    },
    async loadWorks(append) {
      const filterParam = this.entityFilterParam()
      if (!filterParam) {
        return
      }
      const nextPage = append ? this.worksPage + 1 : 1
      if (append) {
        this.worksLoadingMore = true
      } else {
        this.worksLoading = true
      }
      try {
        const response = await searchScholar({
          q: '',
          page: nextPage,
          size: this.worksSize,
          oaOnly: false,
          hasPdf: false,
          sort: 'cited',
          [filterParam]: this.entityId
        })
        const incoming = response.results || []
        this.works = append ? this.works.concat(incoming) : incoming
        this.worksPage = nextPage
        this.total = response.total || this.works.length
      } catch (e) {
        if (!append) {
          this.works = []
          this.total = 0
        }
      } finally {
        this.worksLoading = false
        this.worksLoadingMore = false
      }
    },
    loadMoreWorks() {
      if (!this.canLoadMoreWorks) {
        return
      }
      this.loadWorks(true)
    },
    entityFilterParam() {
      const map = {
        authors: 'authorId',
        institutions: 'institutionId',
        sources: 'sourceId',
        topics: 'topicId',
        keywords: 'keywordId',
        publishers: 'publisherId',
        funders: 'funderId'
      }
      return map[this.entityType]
    },
    searchEntityWorks() {
      const filterParam = this.entityFilterParam()
      const query = {
        q: this.$route.query.q || ''
      }
      if (filterParam) {
        query[filterParam] = this.entityId
        query[filterParam.replace('Id', 'Name')] = this.entity && this.entity.name ? this.entity.name : this.fallbackName
      }
      this.$router.push({ path: '/scholar', query })
    },
    openTopic(topic) {
      const id = this.shortId(topic && topic.id)
      if (!id) {
        return
      }
      this.$router.push({
        path: '/scholar/entity',
        query: {
          provider: this.provider,
          entityType: 'topics',
          id,
          name: topic.name || ''
        }
      })
    },
    roleTargetType(role) {
      const roleMap = {
        author: 'authors',
        authors: 'authors',
        institution: 'institutions',
        institutions: 'institutions',
        source: 'sources',
        sources: 'sources',
        publisher: 'publishers',
        publishers: 'publishers',
        funder: 'funders',
        funders: 'funders',
        topic: 'topics',
        topics: 'topics'
      }
      const fromRole = roleMap[String(role && role.role ? role.role : '').toLowerCase()]
      return fromRole || this.entityTypeFromOpenAlexId(role && role.id)
    },
    openRole(role) {
      const entityType = this.roleTargetType(role)
      const id = this.shortId(role && role.id)
      if (!entityType || !id) {
        return
      }
      this.$router.push({
        path: '/scholar/entity',
        query: {
          provider: this.provider,
          entityType,
          id,
          name: this.roleText(role && role.role)
        }
      })
    },
    repositoryTargetId(repository) {
      return this.shortId(repository && repository.id)
    },
    openRepository(repository) {
      const id = this.repositoryTargetId(repository)
      if (!id) {
        return
      }
      const entityType = this.entityTypeFromOpenAlexId(id) || 'sources'
      this.$router.push({
        path: '/scholar/entity',
        query: {
          provider: this.provider,
          entityType,
          id,
          name: repository.name || ''
        }
      })
    },
    openWork(work) {
      const ref = work && work.providerRefs && work.providerRefs.length ? work.providerRefs[0] : null
      if (!ref || !ref.provider || !ref.providerId) {
        return
      }
      this.$router.push({
        path: '/scholar/detail',
        query: {
          provider: ref.provider,
          id: ref.providerId,
          q: this.$route.query.q || ''
        }
      })
    },
    backToSearch() {
      const returnUrl = safeScholarReturnUrl(this.$route.query.returnUrl)
      if (returnUrl) {
        const returnScroll = scholarReturnScrollTop(this.$route.query.returnScroll)
        this.$router.push(returnUrl).then(() => {
          restoreScholarScroll(returnScroll)
        }).catch(() => {
          restoreScholarScroll(returnScroll)
        })
        return
      }
      if (window.history.length > 1) {
        this.$router.back()
        return
      }
      this.$router.push({ path: '/scholar', query: { q: this.$route.query.q || '' } })
    },
    syncUserInfoFromStorage() {
      const authContext = localStorage.getItem('auth-context')
      if (!authContext) {
        this.resetHeaderUserState()
        return
      }
      try {
        const users = JSON.parse(authContext)
        this.resetHeaderUserState()
        users.forEach(item => {
          if (item.type === 'Person' || item.type === 'Child') {
            this.userInfo = this.safeDecodeName(item)
            this.userId = item.name
            this.userType = item.type === 'Person' ? '0' : '1'
            if (!item.isRoaming) {
              const roaming = localStorage.getItem(this.userId)
              this.showRoaming = !roaming || roaming !== '1'
            } else {
              this.userType = '2'
              this.showRoaming = false
            }
            if (this.groupUserInfo === '') {
              this.groupUserInfo = '机构登录'
            }
            this.showloginOut = true
          }
          if (item.type === 'Group') {
            this.groupUserInfos.push(item)
            this.groupUserInfo = this.safeDecodeName(item)
          }
        })
      } catch (e) {
        this.resetHeaderUserState()
      }
    },
    syncHeaderUserStatus() {
      const token = localStorage.getItem('token')
      if (!token) {
        this.syncUserInfoFromStorage()
        return
      }
      authServiceClient
        .getUserStatus(userStatusRequest, { token })
        .then(res => {
          if (!res.getStatus()) {
            localStorage.removeItem('token')
            localStorage.removeItem('auth-context')
            this.resetHeaderUserState()
            return
          }
          this.syncUserInfoFromStorage()
        })
        .catch(() => {
          localStorage.removeItem('token')
          localStorage.removeItem('auth-context')
          this.resetHeaderUserState()
        })
    },
    resetHeaderUserState() {
      this.userInfo = ''
      this.groupUserInfo = ''
      this.groupUserInfos = []
      this.showloginOut = false
      this.showRoaming = false
      this.userId = ''
      this.userType = ''
    },
    safeDecodeName(item) {
      if (!item) {
        return ''
      }
      if (item.realName !== '' && item.realName !== undefined) {
        try {
          return Base64.decode(item.realName)
        } catch (e) {
          return item.name || ''
        }
      }
      return item.name || ''
    },
    handleAuthMessage(e) {
      const data = e && e.data && e.data.data
      if (data === 'params') {
        this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
      }
      if (data === 'close' && typeof layer !== 'undefined') {
        layer.closeAll()
        this.syncHeaderUserStatus()
      }
    },
    gotoPage(type) {
      if (type === 'text') {
        this.toPath('/translate', 'txt')
      } else if (type === 'doc') {
        this.toPath('/translate', 'doc')
      } else if (type === 'ai') {
        this.toPath('/translate', 'AITranslate')
      } else if (type === 'lange') {
        this.toPath('/translate', 'more')
      } else if (type === 'img') {
        this.toPath('/translate', 'img')
      } else if (type === 'polish') {
        this.toPath('/translate', 'polish')
      } else if (type === 'aiPPT') {
        this.toPath('/translate', 'aiPPT')
      } else if (type === 'rag') {
        this.toPath('/rag')
      } else if (type === 'pro') {
        this.toPath('/trans-pro')
      }
    },
    logOut(val) {
      if (val === 'toCenter') {
        window.open('http://user.wesitrans.com', '_blank')
        return
      }
      if (val === 'toAccount') {
        window.open('https://user.wesitrans.com/account', '_blank')
        return
      }
      if (val === 'toOrder') {
        window.open('https://user.wesitrans.com/order', '_blank')
        return
      }
      if (val === 'toTransPro') {
        this.toPath('/trans-pro')
        return
      }
      if (val === 'standLangSet') {
        this.toPath('/translate', 'setting')
        return
      }
      if (val === 'convertDoc') {
        this.toPath('/translate', 'convert')
        return
      }
      if (val === 'mySelfDoc') {
        this.toPath('/list-pro')
        return
      }
      if (val === 'webTranslate') {
        this.toPath('/translate', 'plugin')
        return
      }
      if (val === 'smartClassify') {
        this.toPath('/smart-classify')
        return
      }
      const request = outRequest
      request.setName(val === undefined ? this.userInfo : val)
      authServiceClient
        .logOut(request, {
          token: localStorage.getItem('token')
        })
        .then(() => {
          window.location.reload()
          this.$router.push('/index')
        })
        .catch(() => {
          localStorage.removeItem('token')
          localStorage.removeItem('auth-context')
          this.resetHeaderUserState()
          this.$router.push('/index')
          this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
        })
    },
    toCenter() {
      window.open('https://user.wesitrans.com/account', '_blank')
    },
    getQrcode() {
      this.qrcodeUrl = 'http://common.wesitrans.com/common/roaming/qrcode'
      this.isRoamingTimer = setInterval(this.roamingEvent, 1000)
      this.isRoamingTimerOut = setTimeout(this.clearTime, 60000)
    },
    clearTime() {
      clearInterval(this.isRoamingTimer)
      clearTimeout(this.isRoamingTimerOut)
    },
    roamingEvent() {
      if (this.userId && localStorage.getItem(this.userId) !== '1') {
        const request = checkRoamingRequest
        request.setUserId(this.userId)
        translateServiceClient
          .checkRoaming(request, {
            token: localStorage.getItem('token')
          })
          .then(res => {
            localStorage.setItem(this.userId, res.getMessage())
            if (res.getMessage() === '1') {
              window.location.reload()
            }
          })
          .catch(() => {})
      }
    },
    removeQrcode() {
      this.qrcodeUrl = ''
      clearInterval(this.isRoamingTimer)
      clearTimeout(this.isRoamingTimerOut)
    },
    toPath(url, type) {
      if (url === '/trans-pro') {
        window.open('/trans-pro/index', '_self')
        return
      }
      if (url === '/list' || url === '/list-pro') {
        authServiceClient
          .getUserStatus(userStatusRequest, {
            token: localStorage.getItem('token')
          })
          .then(res => {
            if (!res.getStatus()) {
              localStorage.removeItem('token')
              localStorage.removeItem('auth-context')
              if (this.hasH5) {
                this.$router.push('/h5/login')
                return
              }
              this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
              return
            }
            if (localStorage.getItem('auth-context') && localStorage.getItem('token')) {
              authServiceClient
                .checkUserPower(userPowerRequest, {
                  token: localStorage.getItem('token')
                })
                .then(powerRes => {
                  if (powerRes.getStatus()) {
                    if (type) {
                      this.$router.push({
                        path: url,
                        query: { type }
                      })
                      return
                    }
                    this.$router.push(url)
                    return
                  }
                  this.$message({
                    center: true,
                    type: 'error',
                    showClose: true,
                    duration: 3000,
                    message: powerRes.getMessage()
                  })
                  this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
                })
                .catch(() => {
                  this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
                })
            }
          })
          .catch(() => {
            this.showIframe(process.env.VUE_APP_WEB_LOGIN_URL || '/login')
          })
        return
      }
      if (type) {
        this.$router.push({
          path: url,
          query: { type }
        })
        return
      }
      this.$router.push(url)
    },
    showIframe(url) {
      const index = this.$authPopup.open(url)
      if (index === null) {
        this.$router.push(url)
      }
      return index
    },
    openExternal(url) {
      return openExternalWithConfirm(this, url)
    },
    normalizeEntityType(type) {
      const value = String(type || '').toLowerCase()
      const map = {
        author: 'authors',
        institution: 'institutions',
        source: 'sources',
        topic: 'topics',
        keyword: 'keywords',
        publisher: 'publishers',
        funder: 'funders'
      }
      return map[value] || value
    },
    entityTypeFromOpenAlexId(value) {
      const id = this.shortId(value)
      const prefix = String(id || '').charAt(0).toUpperCase()
      const map = {
        A: 'authors',
        I: 'institutions',
        S: 'sources',
        P: 'publishers',
        F: 'funders',
        T: 'topics'
      }
      return map[prefix] || ''
    },
    entityIdLabel(key) {
      const map = {
        openalex: 'OpenAlex',
        orcid: 'ORCID',
        ror: 'ROR',
        issn: 'ISSN',
        issn_l: 'ISSN-L',
        issnL: 'ISSN-L',
        wikidata: 'Wikidata',
        wikipedia: 'Wikipedia',
        mag: 'MAG',
        grid: 'GRID',
        fatcat: 'Fatcat'
      }
      return map[key] || key
    },
    entityIdValue(value) {
      if (value === null || value === undefined) {
        return ''
      }
      if (Array.isArray(value)) {
        return value.filter(item => item !== null && item !== undefined && String(item).trim() !== '').join('、')
      }
      if (typeof value === 'object') {
        return Object.keys(value)
          .map(key => `${this.entityIdLabel(key)} ${value[key]}`)
          .join('、')
      }
      return String(value).trim()
    },
    roleText(role) {
      const map = {
        author: '作者身份',
        institution: '机构身份',
        source: '来源身份',
        publisher: '出版商身份',
        funder: '基金机构身份',
        topic: '主题身份'
      }
      const value = String(role || '').toLowerCase()
      return map[value] || role || '关联实体'
    },
    metric(key) {
      return this.entity && this.entity.metrics ? this.entity.metrics[key] : null
    },
    extra(key) {
      return this.entity && this.entity.extra ? this.entity.extra[key] : null
    },
    yearPercent(item) {
      const value = Number(item.citedByCount || item.worksCount || 0)
      return `${Math.max(4, Math.round((value / this.maxYearValue) * 100))}%`
    },
    shortId(value) {
      if (!value) {
        return ''
      }
      const text = String(value)
      const index = text.lastIndexOf('/')
      return index >= 0 ? text.substring(index + 1) : text
    },
    shorten(text, size) {
      const value = text || ''
      return value.length > size ? `${value.slice(0, size)}...` : value
    },
    workAuthorsText(work, limit = 6) {
      const authors = work && work.authors ? work.authors : []
      const names = authors.map(item => item && item.name).filter(Boolean)
      if (!names.length) {
        return '未知作者'
      }
      const displayed = names.slice(0, limit).join('、')
      return names.length > limit ? `${displayed} 等` : displayed
    },
    workAvailabilityChips(work) {
      const extra = work && work.extra ? work.extra : {}
      const chips = []
      const push = (key, label, tone = 'is-ok') => {
        if (!chips.some(item => item.key === key)) {
          chips.push({ key, label, tone })
        }
      }
      if (work && work.doi) {
        push('doi', 'DOI')
      }
      if (work && work.abstractText) {
        push('abstract', '摘要')
      }
      if (work && work.pdfUrl) {
        push('pdf', 'PDF')
      }
      if (extra.hasFulltext) {
        push('fulltext', '全文可检索')
      }
      if (work && work.isOa && !work.pdfUrl) {
        push('oa', 'OA')
      }
      if (extra.isRetracted) {
        push('retracted', '已撤稿', 'is-danger')
      }
      if (extra.isParatext) {
        push('paratext', '非正文', 'is-warn')
      }
      return chips
    },
    async copyWorkDoi(work) {
      const doi = work && work.doi ? String(work.doi).trim() : ''
      if (!doi) {
        this.$message.warning('暂无 DOI')
        return
      }
      try {
        await this.copyText(doi)
        this.$message.success('DOI 已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    doiUrl(work) {
      return work && work.doi ? `https://doi.org/${work.doi}` : ''
    },
    firstProviderUrl(work) {
      const refs = work && work.providerRefs ? work.providerRefs : []
      const ref = refs.find(item => item && item.url) || refs[0]
      return ref && ref.url ? ref.url : ''
    },
    firstProviderRef(work) {
      const refs = work && Array.isArray(work.providerRefs) ? work.providerRefs : []
      return refs.find(item => item && item.provider === this.provider && item.providerId) ||
        refs.find(item => item && item.provider && item.providerId) ||
        refs[0] ||
        null
    },
    workPdfKey(work) {
      const ref = this.firstProviderRef(work)
      return (work && (work.workKey || work.doi || work.title)) || (ref && `${ref.provider}:${ref.providerId}`) || ''
    },
    isWorkPdfDownloading(work) {
      const key = this.workPdfKey(work)
      return !!(key && this.pdfDownloadingKeys[key])
    },
    pdfFallbackUrl(work) {
      return work && (work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)) || ''
    },
    pdfFilenameForWork(work) {
      const title = work && work.title ? work.title : 'scholar-work'
      return `${this.safeFilename(title)}.pdf`
    },
    async downloadWorkPdf(work, event) {
      if (event && typeof event.stopPropagation === 'function') {
        event.stopPropagation()
      }
      if (!work || !work.pdfUrl) {
        this.$message.warning('暂无可下载 PDF')
        return
      }
      const ref = this.firstProviderRef(work)
      if (!ref || !ref.provider || !ref.providerId) {
        this.$message.warning('暂无可下载 PDF')
        return
      }
      const key = this.workPdfKey(work)
      if (key && this.pdfDownloadingKeys[key]) {
        return
      }
      if (key) {
        this.$set(this.pdfDownloadingKeys, key, true)
      }
      try {
        const result = await downloadScholarWorkPdf(ref.provider, ref.providerId)
        this.saveBlob(result.blob, result.filename || this.pdfFilenameForWork(work))
        this.$message.success('下载已开始')
      } catch (e) {
        await this.handlePdfDownloadFailure(work, e)
      } finally {
        if (key) {
          this.$delete(this.pdfDownloadingKeys, key)
        }
      }
    },
    async handlePdfDownloadFailure(work) {
      const pdfUrl = work && work.pdfUrl ? work.pdfUrl : ''
      const fallbackUrl = this.pdfFallbackUrl(work)
      const message = '当前文献暂未返回可直接下载的 PDF，可先复制 PDF 链接，或打开原文页手动下载。'
      if (!pdfUrl && !fallbackUrl) {
        this.$message.warning('当前文献暂未返回可直接下载的 PDF')
        return
      }
      try {
        await this.$confirm(
          message,
          'PDF 下载未完成',
          {
            confirmButtonText: pdfUrl ? '复制 PDF 链接' : '知道了',
            cancelButtonText: fallbackUrl ? '打开原文' : '取消',
            distinguishCancelAndClose: true,
            type: 'warning'
          }
        )
        if (pdfUrl) {
          await this.copyText(pdfUrl)
          this.$message.success('PDF 链接已复制')
        }
      } catch (action) {
        if (action === 'cancel' && fallbackUrl) {
          await this.openExternal(fallbackUrl)
        }
      }
    },
    worksMarkdownText() {
      const title = this.entity && this.entity.name ? this.entity.name : this.fallbackName
      const lines = [
        `# ${title} - 代表文献清单`,
        '',
        `- 实体类型：${this.entityTypeText}`,
        `- 已加载：${this.formatNumber(this.works.length)} / ${this.formatNumber(this.total || this.works.length)} 篇`,
        '',
        '## 文献'
      ]
      this.works.forEach((work, index) => {
        const url = work.landingPageUrl || this.doiUrl(work) || this.firstProviderUrl(work)
        lines.push('', `### ${index + 1}. ${work.title || 'Untitled work'}`)
        lines.push(`- 作者：${this.workAuthorsText(work)}`)
        lines.push(`- 年份：${work.publicationYear || '未知年份'}`)
        lines.push(`- 来源：${work.sourceName || '未知来源'}`)
        lines.push(`- 引用数：${this.formatNumber(work.citedByCount || 0)}`)
        lines.push(`- 开放获取：${work.isOa ? '是' : '否'}`)
        if (work.doi) {
          lines.push(`- DOI：${this.doiUrl(work)}`)
        }
        if (url) {
          lines.push(`- 链接：${url}`)
        }
        if (work.abstractText) {
          lines.push(`- 摘要：${this.shorten(String(work.abstractText).replace(/\s+/g, ' ').trim(), 260)}`)
        }
      })
      return lines.join('\n')
    },
    currentEntityLink() {
      const query = {
        provider: this.provider,
        entityType: this.entityType,
        id: this.entityId,
        name: this.entity && this.entity.name ? this.entity.name : this.fallbackName
      }
      if (this.$route.query.q) {
        query.q = this.$route.query.q
      }
      const route = this.$router.resolve({ path: '/scholar/entity', query })
      const origin = window.location.origin || ''
      return `${origin}${route.href}`
    },
    entityProfileMarkdownText() {
      const title = this.entity && this.entity.name ? this.entity.name : this.fallbackName
      const externalUrl = this.homepageUrl || (this.entity && this.entity.url) || ''
      const basicRows = [
        [this.externalIdLabel, this.entity && this.entity.externalId],
        ['国家/地区', this.extra('countryCode')],
        ['类型', this.extra('type')],
        ['ISSN-L', this.extra('issnL')],
        ['主办机构', this.extra('hostOrganizationName')],
        ['学科层级', [this.extra('domain'), this.extra('field'), this.extra('subfield')].filter(Boolean).join(' / ')]
      ].filter(([, value]) => value !== null && value !== undefined && String(value).trim() !== '')
      const lines = [
        `# ${title} - 实体概览`,
        '',
        `- 实体类型：${this.entityTypeText}`,
        `- 作品数：${this.formatNumber(this.entity.worksCount || this.metric('worksCount') || 0)}`,
        `- 引用数：${this.formatNumber(this.entity.citedByCount || this.metric('citedByCount') || 0)}`,
        `- H-index：${this.metric('hIndex') || '-'}`,
        `- I10-index：${this.metric('i10Index') || '-'}`,
        `- 平台链接：${this.currentEntityLink()}`
      ]
      if (externalUrl) {
        lines.push(`- 外部链接：${externalUrl}`)
      }
      if (this.worksApiUrl) {
        lines.push(`- 三方作品接口：${this.worksApiUrl}`)
      }
      if (this.entity.hint || this.extraText) {
        lines.push(`- 简介：${this.entity.hint || this.extraText}`)
      }
      if (this.entityDescription) {
        lines.push('', '## 简介', this.entityDescription)
      }
      if (basicRows.length) {
        lines.push('', '## 基础信息')
        basicRows.forEach(([label, value]) => {
          lines.push(`- ${label}：${value}`)
        })
      }
      if (this.entityExternalIds.length) {
        lines.push('', '## 外部标识')
        this.entityExternalIds.forEach(item => {
          lines.push(`- ${item.label}：${item.value}`)
        })
      }
      if (this.entityRoles.length) {
        lines.push('', '## 关联角色')
        this.entityRoles.forEach(role => {
          lines.push(`- ${this.roleText(role.role)}：${this.formatNumber(role.worksCount || 0)} 作品${role.id ? `（${role.id}）` : ''}`)
        })
      }
      if (this.entityRepositories.length) {
        lines.push('', '## 相关仓储')
        this.entityRepositories.forEach(repository => {
          const extra = repository.hostOrganization ? `，主办机构 ${repository.hostOrganization}` : ''
          lines.push(`- ${repository.name || repository.id}${extra}`)
        })
      }
      if (this.countsByYear.length) {
        lines.push('', '## 年度趋势')
        this.countsByYear.forEach(item => {
          const values = [
            item.worksCount !== undefined ? `作品 ${this.formatNumber(item.worksCount)}` : '',
            item.citedByCount !== undefined ? `引用 ${this.formatNumber(item.citedByCount)}` : ''
          ].filter(Boolean).join('，')
          lines.push(`- ${item.year || '-'}：${values || this.formatNumber(item.count || 0)}`)
        })
      }
      if (this.entityTopics.length) {
        lines.push('', '## 相关主题')
        this.entityTopics.forEach(topic => {
          lines.push(`- ${topic.name || topic.id}`)
        })
      }
      if (this.works.length) {
        lines.push('', '## 已加载代表文献')
        this.works.slice(0, 5).forEach((work, index) => {
          lines.push(`- ${index + 1}. ${work.title || 'Untitled work'}（${work.publicationYear || '-'}，引 ${this.formatNumber(work.citedByCount || 0)}）`)
        })
      }
      return lines.join('\n')
    },
    async copyEntityProfile() {
      if (!this.entity) {
        return
      }
      try {
        await this.copyText(this.entityProfileMarkdownText())
        this.$message.success('实体概览已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadEntityProfile() {
      if (!this.entity) {
        return
      }
      const filename = `${this.safeFilename(this.entity && this.entity.name ? this.entity.name : this.fallbackName)}-profile.md`
      this.downloadText(this.entityProfileMarkdownText(), filename, 'text/markdown;charset=utf-8')
      this.$message.success('实体概览已导出')
    },
    handleEntityMoreCommand(command) {
      if (command === 'copy-profile') {
        this.copyEntityProfile()
        return
      }
      if (command === 'download-profile') {
        this.downloadEntityProfile()
      }
    },
    yearTrendCsvText() {
      const title = this.entity && this.entity.name ? this.entity.name : this.fallbackName
      const rows = [
        ['entity_type', 'entity_name', 'year', 'works_count', 'cited_by_count', 'count'],
        ...this.countsByYear.map(item => [
          this.entityTypeText,
          title,
          item.year || '',
          item.worksCount !== undefined && item.worksCount !== null ? item.worksCount : '',
          item.citedByCount !== undefined && item.citedByCount !== null ? item.citedByCount : '',
          item.count !== undefined && item.count !== null ? item.count : ''
        ])
      ]
      return rows.map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    async copyYearTrend() {
      if (!this.countsByYear.length) {
        return
      }
      const title = this.entity && this.entity.name ? this.entity.name : this.fallbackName
      const lines = [
        `${title} 年度趋势`,
        ...this.countsByYear.map(item => {
          const values = [
            item.worksCount !== undefined ? `作品 ${this.formatNumber(item.worksCount)}` : '',
            item.citedByCount !== undefined ? `引用 ${this.formatNumber(item.citedByCount)}` : '',
            item.count !== undefined ? `数量 ${this.formatNumber(item.count)}` : ''
          ].filter(Boolean).join('，')
          return `${item.year || '-'}：${values || '-'}`
        })
      ]
      try {
        await this.copyText(lines.join('\n'))
        this.$message.success('年度趋势已复制')
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadYearTrend() {
      if (!this.countsByYear.length) {
        return
      }
      const filename = `${this.safeFilename(this.entity && this.entity.name ? this.entity.name : this.fallbackName)}-year-trend.csv`
      this.downloadText(`\uFEFF${this.yearTrendCsvText()}`, filename, 'text/csv;charset=utf-8')
      this.$message.success('年度趋势已导出')
    },
    async copyWorksMarkdown() {
      if (!this.works.length) {
        return
      }
      try {
        await this.copyText(this.worksMarkdownText())
        this.$message.success(`已复制 ${this.works.length} 篇代表文献`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadWorksMarkdown() {
      if (!this.works.length) {
        return
      }
      const filename = `${this.safeFilename(this.entity && this.entity.name ? this.entity.name : this.fallbackName)}-works.md`
      this.downloadText(this.worksMarkdownText(), filename, 'text/markdown;charset=utf-8')
      this.$message.success(`已导出 ${this.works.length} 篇代表文献`)
    },
    handleWorksCitationCommand(command) {
      const [action, format] = String(command || '').split(':')
      if (!action || !format) {
        return
      }
      if (action === 'copy') {
        this.copyWorksCitationLibrary(format)
        return
      }
      if (action === 'export') {
        this.downloadWorksCitationLibrary(format)
      }
    },
    handleWorksMoreCommand(command) {
      const [action, format] = String(command || '').split(':')
      if (action === 'copy' && format === 'markdown') {
        this.copyWorksMarkdown()
        return
      }
      if (action === 'export' && format === 'markdown') {
        this.downloadWorksMarkdown()
        return
      }
      this.handleWorksCitationCommand(command)
    },
    toggleWorksActions() {
      this.worksActionsVisible = !this.worksActionsVisible
      if (this.worksActionsVisible) {
        this.activeCitationWorkKey = ''
      }
    },
    runWorksMoreCommand(command) {
      this.worksActionsVisible = false
      this.handleWorksMoreCommand(command)
    },
    workCitationActionKey(work) {
      if (!work) {
        return ''
      }
      return this.workPdfKey(work) || work.workKey || work.doi || work.title || ''
    },
    isWorkCitationPanelOpen(work) {
      const key = this.workCitationActionKey(work)
      return !!key && this.activeCitationWorkKey === key
    },
    toggleWorkCitationPanel(work) {
      const key = this.workCitationActionKey(work)
      if (!key) {
        return
      }
      this.activeCitationWorkKey = this.activeCitationWorkKey === key ? '' : key
      if (this.activeCitationWorkKey) {
        this.worksActionsVisible = false
      }
    },
    runSingleWorkCitationCommand(command, work) {
      this.activeCitationWorkKey = ''
      this.handleSingleWorkCitationCommand(command, work)
    },
    worksCitationLibraryText(format) {
      if (format === 'screening') {
        return this.worksScreeningSheetCsv()
      }
      return formatWorks(this.works || [], format)
    },
    worksCitationFilename(format) {
      const title = this.entity && this.entity.name ? this.entity.name : this.fallbackName
      const extensionMap = {
        bibtex: 'bib',
        ris: 'ris',
        csv: 'csv',
        screening: 'csv'
      }
      const suffix = format === 'screening' ? 'screening-sheet' : 'citation-library'
      return `${this.safeFilename(title)}-${suffix}.${extensionMap[format] || 'txt'}`
    },
    worksCitationMimeType(format) {
      const typeMap = {
        bibtex: 'application/x-bibtex;charset=utf-8',
        ris: 'application/x-research-info-systems;charset=utf-8',
        csv: 'text/csv;charset=utf-8',
        screening: 'text/csv;charset=utf-8'
      }
      return typeMap[format] || 'text/plain;charset=utf-8'
    },
    async handleSingleWorkCitationCommand(command, work) {
      const [action, format] = String(command || '').split(':')
      if (!action || !format || !work) {
        return
      }
      const content = this.singleWorkCitationText(work, format)
      if (action === 'copy') {
        try {
          await this.copyText(content)
          this.$message.success('引用已复制')
        } catch (e) {
          this.$message.error('复制失败，请手动选择文本复制')
        }
        return
      }
      if (action === 'export') {
        const shouldAddBom = format === 'csv'
        this.downloadText(
          shouldAddBom ? `\uFEFF${content}` : content,
          this.singleWorkCitationFilename(work, format),
          this.worksCitationMimeType(format)
        )
        this.$message.success('引用文件已导出')
      }
    },
    singleWorkCitationText(work, format) {
      return formatWorks([work], format)
    },
    singleWorkCitationFilename(work, format) {
      const extensionMap = {
        bibtex: 'bib',
        ris: 'ris',
        csv: 'csv'
      }
      const title = work && work.title ? work.title : 'scholar-work'
      return `${this.safeFilename(title)}.${extensionMap[format] || 'txt'}`
    },
    worksScreeningSheetCsv() {
      const title = this.entity && this.entity.name ? this.entity.name : this.fallbackName
      const headers = [
        'Screening Decision',
        'Exclude Reason',
        'Reviewer',
        'Notes',
        'Entity Type',
        'Entity Name',
        'Title',
        'Authors',
        'Year',
        'Source',
        'Type',
        'Citations',
        'OA',
        'Has PDF',
        'Has Abstract',
        'DOI',
        'PDF URL',
        'Landing URL',
        'Provider URL',
        'Abstract',
        'Topics'
      ]
      const rows = (this.works || []).filter(Boolean).map(work => [
        '',
        '',
        '',
        '',
        this.entityTypeText,
        title,
        work.title || '',
        this.workAuthorsText(work, 20),
        work.publicationYear || '',
        work.sourceName || '',
        work.type || '',
        work.citedByCount || 0,
        work.isOa ? 'yes' : 'no',
        work.pdfUrl ? 'yes' : 'no',
        work.abstractText ? 'yes' : 'no',
        work.doi || '',
        work.pdfUrl || '',
        work.landingPageUrl || '',
        this.firstProviderUrl(work) || '',
        work.abstractText ? String(work.abstractText).replace(/\s+/g, ' ').trim() : '',
        (work.topics || []).map(item => item && item.name).filter(Boolean).join('; ')
      ])
      return [headers].concat(rows).map(row => row.map(this.csvCell).join(',')).join('\n')
    },
    async copyWorksCitationLibrary(format) {
      if (!this.works.length) {
        return
      }
      try {
        await this.copyText(this.worksCitationLibraryText(format))
        this.$message.success(`已复制 ${this.works.length} 篇代表文献引用库`)
      } catch (e) {
        this.$message.error('复制失败，请手动选择文本复制')
      }
    },
    downloadWorksCitationLibrary(format) {
      if (!this.works.length) {
        return
      }
      const content = format === 'csv' || format === 'screening'
        ? `\uFEFF${this.worksCitationLibraryText(format)}`
        : this.worksCitationLibraryText(format)
      this.downloadText(content, this.worksCitationFilename(format), this.worksCitationMimeType(format))
      this.$message.success(`已导出 ${this.works.length} 篇代表文献引用库`)
    },
    async copyText(text) {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(text)
        return
      }
      const textarea = document.createElement('textarea')
      textarea.value = text
      textarea.setAttribute('readonly', 'readonly')
      textarea.style.position = 'fixed'
      textarea.style.left = '-9999px'
      document.body.appendChild(textarea)
      textarea.select()
      const success = document.execCommand('copy')
      document.body.removeChild(textarea)
      if (!success) {
        throw new Error('copy failed')
      }
    },
    downloadText(content, filename, type) {
      const blob = new Blob([content], { type: type || 'text/plain;charset=utf-8' })
      this.saveBlob(blob, filename)
    },
    saveBlob(blob, filename) {
      const url = window.URL.createObjectURL(blob)
      const link = document.createElement('a')
      link.href = url
      link.download = filename
      link.style.display = 'none'
      document.body.appendChild(link)
      link.click()
      document.body.removeChild(link)
      window.setTimeout(() => window.URL.revokeObjectURL(url), 1000)
    },
    safeFilename(value) {
      return String(value || 'scholar-entity')
        .replace(/[\\/:*?"<>|\u0000-\u001f]+/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .slice(0, 80) || 'scholar-entity'
    },
    csvCell(value) {
      const text = String(value === null || value === undefined ? '' : value)
      return `"${text.replace(/"/g, '""')}"`
    },
    formatNumber(value) {
      return Number(value || 0).toLocaleString()
    },
    resolveErrorMessage(error, fallback) {
      const data = error && error.response && error.response.data
      return (data && (data.message || data.error)) || fallback
    }
  }
}
</script>

<style lang="less" scoped src="../assets/v2/css/trans/common.less"/>
<style lang="less" scoped>
.scholar-entity-page {
  min-width: 1200px;
  min-height: 100vh;
  background: var(--bg-page);
  font-family: Microsoft YaHei;
}

.scholar-entity-shell {
  min-height: 100vh;
  background:
    radial-gradient(circle at 82% 180px, rgba(16, 185, 129, 0.42), transparent 280px),
    linear-gradient(180deg, rgba(232, 246, 255, 0.95) 0%, rgba(245, 247, 251, 0.98) 360px, var(--bg-page) 100%);
}

.scholar-trans-header {
  position: relative;
  z-index: 1;
  min-width: 1200px;
  height: 70px;
  margin-top: -1px;
  align-items: center;
  justify-content: center;
  background: #ffffff;
  background-size: 1929px 79px;
  box-shadow: 0px 0px 5px 0px rgba(0, 0, 0, 0.18);
}

.scholar-trans-header .box_2 {
  width: 1200px;
  height: 59px;
  margin: 0;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.scholar-trans-header .image_1 {
  width: 104px;
  height: 28px;
  cursor: pointer;
}

.scholar-trans-header .text_1 {
  width: 182px;
  height: 28px;
  margin: 0 0 0 119px;
  display: flex;
  justify-content: space-between;
  overflow-wrap: break-word;
  color: var(--foreground-secondary);
  font-size: 16px;
  font-family: MicrosoftYaHei;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  white-space: nowrap;
}

.scholar-trans-header .text_1 span {
  width: 40px;
  display: flex;
  align-items: center;
  justify-content: space-between;
  color: var(--foreground-secondary);
  font-size: 16px;
  font-family: MicrosoftYaHei;
  font-weight: 400;
  cursor: pointer;
}

.scholar-trans-header .thumbnail_1 {
  width: 14px;
  height: 14px;
  margin: 6px 0 0 20px;
  cursor: pointer;
}

.scholar-trans-header .text_2 {
  max-width: 80px;
  height: 24px;
  margin: 7px 0 0 8px;
  overflow: hidden;
  overflow-wrap: break-word;
  color: #666666;
  font-size: 16px;
  font-family: MicrosoftYaHei;
  font-weight: 400;
  line-height: 24px;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.scholar-trans-header .thumbnail_4 {
  width: 10px;
  height: 5px;
}

.scholar-entity-main {
  width: 100%;
  margin: 0 auto;
  padding: 34px 24px 80px;
  box-sizing: border-box;
}

.scholar-entity-toolbar {
  margin-bottom: 18px;
}

.scholar-back-btn {
  height: 34px;
  padding: 0 13px;
  border: 1px solid #bfd6e8;
  border-radius: 4px;
  background: rgba(255, 255, 255, 0.82);
  color: #42637d;
  cursor: pointer;
}

.scholar-entity-content {
  box-sizing: border-box;
  min-height: 520px;
  padding: 30px 34px 0;
  border-top: 1px solid rgba(196, 217, 232, 0.86);
  background: rgba(255, 255, 255, 0.78);
}

.scholar-entity-error {
  margin-bottom: 18px;
}

.scholar-entity-head {
  display: grid;
  grid-template-columns: minmax(0, 1fr);
  row-gap: 16px;
  padding: 0 0 22px;
  border-bottom: 1px solid #dfeaf3;
}

.scholar-entity-title-block {
  width: 100%;
  min-width: 0;
  max-width: 100%;
}

.scholar-entity-type {
  display: inline-flex;
  height: 22px;
  align-items: center;
  padding: 0 8px;
  border-radius: 11px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  font-weight: 700;
}

.scholar-entity-head h1 {
  margin: 12px 0 8px;
  color: #182332;
  font-size: 28px;
  font-weight: 700;
  line-height: 1.32;
  overflow-wrap: anywhere;
  word-break: break-word;
}

.scholar-entity-head p {
  margin: 0;
  color: #5d7186;
  font-size: 14px;
  line-height: 1.7;
}

.scholar-entity-actions {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
  min-width: 0;
  padding-top: 0;
}

.scholar-entity-actions /deep/ .el-button {
  height: 34px;
  margin-left: 0;
  padding: 0 14px;
  border-radius: 4px;
  font-size: 13px;
}

.scholar-entity-actions /deep/ .el-button--primary {
  border-color: var(--primary);
  background: var(--primary);
}

.scholar-entity-more-btn {
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-entity-more-btn:hover,
.scholar-entity-more-btn:focus {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-entity-metrics {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin: 22px 0 26px;
  border-top: 1px solid #e3edf5;
  border-bottom: 1px solid #e3edf5;
  background: rgba(248, 252, 255, 0.72);
}

.scholar-entity-metrics div {
  min-width: 0;
  padding: 16px 22px;
  border-right: 1px solid #e3edf5;
}

.scholar-entity-metrics div:last-child {
  border-right: 0;
}

.scholar-entity-metrics strong {
  display: block;
  color: #182332;
  font-size: 23px;
  line-height: 1.2;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}

.scholar-entity-metrics span {
  display: block;
  margin-top: 6px;
  color: #6f8194;
  font-size: 13px;
}

.scholar-entity-layout {
  display: grid;
  grid-template-columns: 292px minmax(0, 1fr);
  gap: 30px;
  align-items: flex-start;
}

.scholar-entity-side {
  min-width: 0;
  padding-right: 20px;
  border-right: 1px solid #e5eef5;
}

.scholar-entity-section {
  padding: 20px 0;
  border-top: 1px solid #e7eff6;
}

.scholar-entity-section:first-child {
  border-top: 0;
  padding-top: 0;
}

.scholar-entity-section h2,
.scholar-entity-work-head h2 {
  margin: 0 0 14px;
  color: #1c2b3a;
  font-size: 16px;
  font-weight: 700;
}

.scholar-entity-section-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 10px;
  margin-bottom: 14px;
}

.scholar-entity-section-head h2 {
  margin-bottom: 0;
}

.scholar-entity-section-actions {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  flex-shrink: 0;
}

.scholar-entity-section-actions button {
  display: inline-flex;
  align-items: center;
  gap: 3px;
  height: 24px;
  padding: 0 7px;
  border: 1px solid #d5e7f3;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  cursor: pointer;
}

.scholar-entity-section-actions button:hover,
.scholar-entity-section-actions button:focus {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
}

.scholar-field-list {
  margin: 0;
}

.scholar-field-list div {
  display: grid;
  grid-template-columns: 76px minmax(0, 1fr);
  gap: 10px;
  margin-bottom: 12px;
}

.scholar-field-list dt {
  color: #7d8fa2;
  font-size: 13px;
}

.scholar-field-list dd {
  margin: 0;
  color: #2a3a4a;
  font-size: 13px;
  line-height: 1.5;
  word-break: break-word;
}

.scholar-entity-description {
  margin: 0;
  color: #40566c;
  font-size: 13px;
  line-height: 1.7;
}

.scholar-role-list,
.scholar-repository-list {
  display: grid;
  gap: 8px;
}

.scholar-role-list button,
.scholar-repository-list button {
  width: 100%;
  min-width: 0;
  padding: 9px 10px;
  border: 1px solid #dbeaf4;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: #294056;
  text-align: left;
  cursor: pointer;
  transition: border-color 0.16s ease, background-color 0.16s ease;
}

.scholar-role-list button:not(:disabled):hover,
.scholar-repository-list button:not(:disabled):hover {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
}

.scholar-role-list button:disabled,
.scholar-repository-list button:disabled {
  cursor: default;
}

.scholar-role-list span,
.scholar-repository-list span {
  display: block;
  min-width: 0;
  overflow: hidden;
  color: #1f3347;
  font-size: 13px;
  font-weight: 700;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-role-list strong,
.scholar-repository-list em {
  display: block;
  margin-top: 4px;
  overflow: hidden;
  color: #74899d;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: 1.4;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.scholar-year-row {
  display: grid;
  grid-template-columns: 42px minmax(0, 1fr) 58px;
  align-items: center;
  gap: 8px;
  margin-bottom: 10px;
  color: #51677d;
  font-size: 12px;
}

.scholar-year-row em {
  height: 6px;
  overflow: hidden;
  border-radius: 3px;
  background: #e7eef5;
}

.scholar-year-row i {
  display: block;
  height: 100%;
  border-radius: 3px;
  background: var(--primary);
}

.scholar-year-row strong {
  color: #6a7f94;
  font-weight: 400;
  text-align: right;
}

.scholar-topic-list {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}

.scholar-topic-list button {
  border: 0;
  border-radius: 12px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  line-height: 24px;
  max-width: 100%;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.scholar-entity-works {
  min-width: 0;
  min-height: 360px;
}

.scholar-entity-work-head {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 8px;
}

.scholar-entity-work-head span {
  color: #789;
  font-size: 13px;
}

.scholar-entity-work-actions {
  display: flex;
  align-items: center;
  justify-content: flex-end;
  flex-wrap: wrap;
  gap: 10px;
}

.scholar-entity-work-actions button {
  height: 28px;
  padding: 0 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  cursor: pointer;
}

.scholar-entity-work-actions button:hover {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-entity-work-actions i {
  margin-left: 3px;
  font-size: 12px;
}

.scholar-entity-work-tools-btn.active,
.scholar-entity-cite-btn.active {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-entity-works-action-panel {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 10px;
  margin: 0 0 10px;
  padding: 10px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: var(--primary-alpha-08);
}

.scholar-entity-works-action-panel section {
  min-width: 0;
  padding: 10px;
  border: 1px solid #e4eef6;
  border-radius: 4px;
  background: #ffffff;
}

.scholar-entity-works-action-panel h3,
.scholar-entity-work-citation-panel strong {
  display: block;
  margin: 0 0 8px;
  color: #24445f;
  font-size: 13px;
  font-weight: 700;
  line-height: 18px;
}

.scholar-entity-works-action-panel section > div,
.scholar-entity-work-citation-panel > div {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 6px;
}

.scholar-entity-works-action-panel button,
.scholar-entity-work-citation-panel button {
  min-width: 0;
  min-height: 30px;
  padding: 6px 8px;
  border: 1px solid var(--border);
  border-radius: 4px;
  background: #f8fbfe;
  color: #38566e;
  font-size: 12px;
  line-height: 17px;
  text-align: left;
  cursor: pointer;
}

.scholar-entity-works-action-panel button:hover,
.scholar-entity-works-action-panel button:focus,
.scholar-entity-work-citation-panel button:hover,
.scholar-entity-work-citation-panel button:focus {
  border-color: var(--border);
  background: var(--primary-alpha-08);
  color: var(--primary);
}

.scholar-entity-work {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 18px;
  min-width: 0;
  padding: 18px 0;
  border-top: 1px solid #e7eff6;
  cursor: pointer;
  transition: background-color 0.16s ease;
}

.scholar-entity-work:first-of-type {
  border-top: 0;
}

.scholar-entity-work:hover {
  background: rgba(240, 248, 253, 0.68);
}

.scholar-entity-more {
  display: block;
  width: 180px;
  height: 34px;
  margin: 18px auto 0;
  border: 1px solid #cde1f0;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 13px;
  cursor: pointer;
}

.scholar-entity-more:disabled {
  color: #96a9ba;
  cursor: not-allowed;
}

.scholar-entity-more:not(:disabled):hover {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
}

.scholar-entity-work-main {
  min-width: 0;
  flex: 1 1 420px;
}

.scholar-entity-work-side {
  display: inline-flex;
  align-items: center;
  flex-shrink: 0;
  gap: 8px;
}

.scholar-entity-cite-btn {
  display: inline-flex;
  height: 24px;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  border: 1px solid #d4e7f3;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  color: var(--primary);
  font-size: 12px;
  cursor: pointer;
}

.scholar-entity-pdf-btn {
  display: inline-flex;
  height: 24px;
  align-items: center;
  gap: 4px;
  padding: 0 8px;
  border: 1px solid var(--primary);
  border-radius: 4px;
  background: var(--primary);
  color: #ffffff;
  font-size: 12px;
  font-weight: 700;
  cursor: pointer;
}

.scholar-entity-pdf-btn:hover,
.scholar-entity-pdf-btn:focus {
  border-color: var(--primary);
  background: var(--primary);
}

.scholar-entity-pdf-btn:disabled {
  cursor: not-allowed;
  opacity: 0.72;
}

.scholar-entity-cite-btn:hover,
.scholar-entity-cite-btn:focus {
  border-color: var(--primary);
  background: var(--primary-alpha-08);
}

.scholar-entity-work-citation-panel {
  flex: 1 1 100%;
  min-width: 0;
  padding: 10px;
  border: 1px solid #e4eef6;
  border-radius: 4px;
  background: var(--primary-alpha-08);
  box-sizing: border-box;
  cursor: default;
}

.scholar-entity-work strong {
  display: block;
  color: #1d2b3a;
  font-size: 15px;
  line-height: 1.45;
  overflow-wrap: anywhere;
}

.scholar-entity-work p {
  margin: 8px 0 0;
  color: #607589;
  font-size: 13px;
  line-height: 1.6;
  overflow-wrap: anywhere;
}

.scholar-entity-work-doi {
  display: flex;
  align-items: center;
  gap: 6px;
  min-width: 0;
  max-width: 100%;
  margin-top: 8px;
  color: #5c7890;
  font-size: 12px;
  line-height: 20px;
}

.scholar-entity-work-doi span {
  flex: 0 0 auto;
  height: 20px;
  padding: 0 6px;
  border-radius: 4px;
  background: #eef5fb;
  color: #607b93;
  font-weight: 700;
}

.scholar-entity-work-doi button {
  display: block;
  min-width: 0;
  padding: 0;
  overflow: hidden;
  border: 0;
  background: transparent;
  color: var(--primary);
  font: inherit;
  text-align: left;
  text-overflow: ellipsis;
  white-space: nowrap;
  cursor: pointer;
}

.scholar-entity-work-doi button:hover,
.scholar-entity-work-doi button:focus {
  color: var(--primary);
  text-decoration: underline;
}

.scholar-entity-work-chips {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-top: 8px;
}

.scholar-entity-work-chip {
  display: inline-flex;
  align-items: center;
  height: 20px;
  padding: 0 7px;
  border-radius: 10px;
  background: #eaf6ff;
  color: var(--primary);
  font-size: 12px;
  line-height: 20px;
  white-space: nowrap;
}

.scholar-entity-work-chip.is-warn {
  background: #fff7ed;
  color: #c46a00;
}

.scholar-entity-work-chip.is-danger {
  background: #fff1f2;
  color: #c6283a;
}

.scholar-work-abstract {
  color: #40566c !important;
  display: -webkit-box;
  overflow: hidden;
  -webkit-line-clamp: 2;
  -webkit-box-orient: vertical;
}

.scholar-oa-badge {
  display: inline-flex;
  height: 22px;
  align-items: center;
  flex-shrink: 0;
  padding: 0 8px;
  border-radius: 11px;
  background: #e1faed;
  color: #12a35c;
  font-size: 12px;
  font-weight: 700;
}

@media (max-width: 1280px) {
  .scholar-entity-main,
  .scholar-trans-header .box_2 {
    width: 1000px;
  }

  .scholar-entity-layout {
    grid-template-columns: 270px minmax(0, 1fr);
    gap: 24px;
  }
}

@media (max-width: 768px) {
  .scholar-entity-page,
  .scholar-entity-shell,
  .scholar-entity-main,
  .scholar-entity-content {
    width: 100%;
    min-width: 0;
    max-width: 100%;
    box-sizing: border-box;
  }

  .scholar-entity-page,
  .scholar-entity-shell {
    overflow-x: hidden;
  }

  .scholar-entity-shell {
    background:
      radial-gradient(circle at 82% 140px, rgba(16, 185, 129, 0.38), transparent 220px),
      linear-gradient(180deg, rgba(232, 246, 255, 0.96) 0%, rgba(245, 247, 251, 0.99) 300px, var(--bg-page) 100%);
  }

  .scholar-entity-main {
    margin: 0;
    padding: 16px 12px calc(32px + env(safe-area-inset-bottom));
  }

  .scholar-entity-toolbar {
    margin-bottom: 12px;
  }

  .scholar-back-btn {
    min-height: 36px;
  }

  .scholar-entity-content {
    min-height: 360px;
    padding: 20px 14px 24px;
    border: 1px solid rgba(196, 217, 232, 0.86);
    border-radius: 6px;
  }

  .scholar-entity-head {
    row-gap: 14px;
    padding-bottom: 18px;
  }

  .scholar-entity-head h1 {
    margin-top: 10px;
    font-size: 23px;
    line-height: 1.38;
  }

  .scholar-entity-actions {
    align-items: stretch;
    gap: 8px;
  }

  .scholar-entity-actions /deep/ .el-button {
    flex: 1 1 calc(50% - 4px);
    min-width: 0;
    min-height: 36px;
    height: auto;
    margin-left: 0;
    padding: 8px 10px;
    white-space: normal;
  }

  .scholar-entity-metrics {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    margin: 18px 0 20px;
  }

  .scholar-entity-metrics div {
    padding: 13px 12px;
    border-bottom: 1px solid #e3edf5;
  }

  .scholar-entity-metrics div:nth-child(2n) {
    border-right: 0;
  }

  .scholar-entity-metrics div:nth-last-child(-n + 2) {
    border-bottom: 0;
  }

  .scholar-entity-metrics strong {
    font-size: 20px;
  }

  .scholar-entity-layout {
    grid-template-columns: minmax(0, 1fr);
    gap: 22px;
  }

  .scholar-entity-side {
    width: 100%;
    min-width: 0;
    padding-right: 0;
    border-right: 0;
    border-bottom: 1px solid #e5eef5;
  }

  .scholar-entity-section {
    padding: 18px 0;
  }

  .scholar-entity-section-head {
    align-items: flex-start;
    flex-wrap: wrap;
  }

  .scholar-entity-section-actions {
    flex-wrap: wrap;
  }

  .scholar-field-list div {
    grid-template-columns: 72px minmax(0, 1fr);
    gap: 8px;
  }

  .scholar-role-list span,
  .scholar-repository-list span,
  .scholar-role-list strong,
  .scholar-repository-list em {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
  }

  .scholar-entity-work-head {
    align-items: flex-start;
    flex-direction: column;
    gap: 8px;
  }

  .scholar-entity-work-head h2 {
    margin-bottom: 0;
  }

  .scholar-entity-work-actions {
    justify-content: flex-start;
    width: 100%;
  }

  .scholar-entity-work-actions button {
    min-height: 32px;
    height: auto;
    padding-top: 6px;
    padding-bottom: 6px;
  }

  .scholar-entity-works-action-panel {
    grid-template-columns: minmax(0, 1fr);
    gap: 8px;
    padding: 8px;
  }

  .scholar-entity-works-action-panel section {
    padding: 9px;
  }

  .scholar-entity-work {
    align-items: stretch;
    flex-direction: column;
    gap: 12px;
    margin-bottom: 10px;
    padding: 14px;
    border: 1px solid #e1ebf3;
    border-radius: 6px;
    background: rgba(255, 255, 255, 0.9);
  }

  .scholar-entity-work:first-of-type {
    border-top: 1px solid #e1ebf3;
  }

  .scholar-entity-work-main {
    flex-basis: auto;
    width: 100%;
  }

  .scholar-entity-work-side {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    width: 100%;
    gap: 8px;
  }

  .scholar-entity-pdf-btn,
  .scholar-entity-cite-btn {
    flex: 1 1 auto;
    justify-content: center;
    min-height: 30px;
    height: auto;
    padding-top: 5px;
    padding-bottom: 5px;
  }

  .scholar-entity-work-doi {
    align-items: flex-start;
  }

  .scholar-entity-work-doi button {
    overflow: visible;
    text-overflow: clip;
    white-space: normal;
    overflow-wrap: anywhere;
    word-break: break-word;
  }

  .scholar-entity-work-citation-panel {
    padding: 9px;
  }

  .scholar-entity-works-action-panel section > div,
  .scholar-entity-work-citation-panel > div {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .scholar-entity-more {
    width: 100%;
    max-width: 260px;
    min-height: 38px;
    height: auto;
  }
}

@media (max-width: 380px) {
  .scholar-entity-main {
    padding-left: 8px;
    padding-right: 8px;
  }

  .scholar-entity-content {
    padding-left: 11px;
    padding-right: 11px;
  }

  .scholar-entity-actions /deep/ .el-button {
    flex-basis: 100%;
  }

  .scholar-entity-metrics {
    grid-template-columns: minmax(0, 1fr);
  }

  .scholar-entity-metrics div,
  .scholar-entity-metrics div:nth-child(2n),
  .scholar-entity-metrics div:nth-last-child(-n + 2) {
    border-right: 0;
    border-bottom: 1px solid #e3edf5;
  }

  .scholar-entity-metrics div:last-child {
    border-bottom: 0;
  }

  .scholar-entity-works-action-panel section > div,
  .scholar-entity-work-citation-panel > div {
    grid-template-columns: minmax(0, 1fr);
  }
}
</style>
